/**
 * Performance monitoring utilities
 * Tracks and logs performance metrics for debugging and optimization
 */

import logger from './logger';

class PerformanceMonitor {
  constructor() {
    this.metrics = {};
    this.marks = {};
    this.isEnabled = process.env.NODE_ENV === 'development';
  }

  /**
   * Mark a performance checkpoint
   */
  mark(name, label) {
    if (!this.isEnabled) return;
    
    const timestamp = performance.now();
    this.marks[name] = { timestamp, label };
    
    if (window.performance && window.performance.mark) {
      window.performance.mark(name);
    }
  }

  /**
   * Measure time between two marks
   */
  measure(name, startMark, endMark) {
    if (!this.isEnabled) return null;
    
    const start = this.marks[startMark]?.timestamp;
    const end = this.marks[endMark]?.timestamp;
    
    if (!start || !end) return null;
    
    const duration = end - start;
    this.metrics[name] = duration;
    
    if (window.performance && window.performance.measure) {
      try {
        window.performance.measure(name, startMark, endMark);
      } catch (e) {
        // Mark might not exist in performance API
      }
    }
    
    return duration;
  }

  /**
   * Get all metrics
   */
  getMetrics() {
    return { ...this.metrics };
  }

  /**
   * Get all marks
   */
  getMarks() {
    return { ...this.marks };
  }

  /**
   * Log performance report
   */
  logReport() {
    if (!this.isEnabled) return;
    
    // Performance metrics available via getMetrics() method
    // Use performanceMonitor.getMetrics() to access data programmatically
  }

  /**
   * Clear all metrics
   */
  clear() {
    this.metrics = {};
    this.marks = {};
  }
}

export const performanceMonitor = new PerformanceMonitor();

/**
 * Hook to measure component render time
 */
export function measureRender(componentName) {
  if (process.env.NODE_ENV !== 'development') return () => {};
  
  const start = performance.now();
  performanceMonitor.mark(`${componentName}-render-start`);
  
  return () => {
    const end = performance.now();
    const duration = end - start;
    performanceMonitor.mark(`${componentName}-render-end`);
    performanceMonitor.measure(`${componentName}-render`,
      `${componentName}-render-start`,
      `${componentName}-render-end`
    );
    
    if (duration > 16) { // Warn if render takes longer than one frame
      logger.warn('Slow component render', { component: componentName, duration: `${duration.toFixed(2)}ms` });
    }
  };
}

/**
 * Measure async operation
 */
export async function measureAsync(name, fn) {
  if (!performanceMonitor.isEnabled) {
    return await fn();
  }
  
  performanceMonitor.mark(`${name}-start`);
  const start = performance.now();
  
  try {
    const result = await fn();
    const duration = performance.now() - start;
    performanceMonitor.mark(`${name}-end`);
    performanceMonitor.measure(name, `${name}-start`, `${name}-end`);
    
    if (duration > 1000) {
      logger.warn('Slow async operation', { name, duration: `${duration.toFixed(2)}ms` });
    }
    
    return result;
  } catch (error) {
    performanceMonitor.mark(`${name}-error`);
    throw error;
  }
}
